import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Tooltip,
  ToolbarButton,
} from '@fluentui/react-components';
import { PanelLeft24Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import Header from '../components/Header';
import SettingsNavSidebar from '../components/SettingsNavSidebar';
import { getActiveSettingsLabel } from '../config/sidebar-config';

const COLLAPSE_KEY = 'twm_settings_nav_collapsed';

// Unified work surface for Settings — same workspace-card + inline-Header shell
// the Orders/Customers list pages use. The left column is the settings
// navigation (SettingsNavSidebar) rather than a filter panel, and the inline
// Header carries a breadcrumb (Settings > Section) since this surface is the one
// place with real hierarchy. Sub-pages render in the scrollable content column
// and own their internal padding.
const useStyles = makeStyles({
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  crumbRoot: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  crumbCurrent: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  crumbRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
});

interface SettingsLayoutProps {
  children: React.ReactNode;
}

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  const styles = useStyles();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      /* private mode — collapse just won't persist */
    }
  }, [collapsed]);

  const leaf = getActiveSettingsLabel(location.pathname);

  return (
    <div className={styles.workspaceCard}>
      <Header
        inline
        hideSelectionInfo
        stackTitleOnMobile
        leftActions={
          <Tooltip
            content={collapsed ? 'Show settings menu' : 'Hide settings menu'}
            relationship="label"
          >
            <ToolbarButton
              icon={<PanelLeft24Regular />}
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Show settings menu' : 'Hide settings menu'}
            />
          </Tooltip>
        }
        customTitle={
          <div className={styles.crumbRow}>
            {/* Plain text, not a link — the settings nav rail already
                provides navigation back to the section index. */}
            <span className={styles.crumbRoot}>Settings</span>
            {leaf && (
              <>
                <ChevronRight16Regular style={{ color: tokens.colorNeutralForeground3 }} />
                <span className={styles.crumbCurrent}>{leaf}</span>
              </>
            )}
          </div>
        }
      />
      <div className={styles.body}>
        <SettingsNavSidebar
          collapsed={collapsed}
          onRequestClose={() => setCollapsed(true)}
        />
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}
